import type PlantModel from "Models/Plant";
import { create } from "zustand";
import ApiService from "Helpers/ApiService";

interface PlantStoreState {
    plants: PlantModel[];
    plantsLoaded: boolean;
    selectedPlantId?: string;
    plantModalOpen: boolean;
    plantModalEdit: boolean;
    getPlant: (id?: string) => PlantModel | undefined;
    fetchPlants: (forceRefresh?: boolean) => Promise<PlantModel[]>;
    getPlantsByName: (name: string) => Promise<PlantModel[]>;
    createPlant: (plant: PlantModel) => Promise<PlantModel>;
    updatePlant: (plant: PlantModel) => Promise<PlantModel>;
    deletePlant: (id: string) => Promise<void>;
    openPlantModal: (plantId?: string, edit?: boolean) => void;
    closePlantModal: () => void;
    deletePlantsById: (ids: string[]) => Promise<void>;
}

let plantsRequest: Promise<PlantModel[]> | undefined;

export const usePlantStore = create<PlantStoreState>((set, get) => ({
    plants: [],
    plantsLoaded: false,
    selectedPlantId: undefined,
    plantModalOpen: false,
    plantModalEdit: false,
    openPlantModal: (selectedPlantId, plantModalEdit = false) =>
        set({ selectedPlantId, plantModalEdit, plantModalOpen: true }),
    closePlantModal: () =>
        set({ selectedPlantId: undefined, plantModalOpen: false, plantModalEdit: false }),
    fetchPlants: async (forceRefresh = false) => {
        if (!forceRefresh && get().plantsLoaded) return get().plants;
        if (plantsRequest) {
            if (!forceRefresh) return plantsRequest;
            await plantsRequest;
            return get().fetchPlants(true);
        }

        plantsRequest = ApiService.getPlants().then((plants: PlantModel[]) => {
            if (!Array.isArray(plants)) {
                throw new Error("Unable to load plants");
            }
            set({ plants, plantsLoaded: true });
            return plants;
        }).finally(() => {
            plantsRequest = undefined;
        });
        
    
        return plantsRequest;
    },
    getPlantsByName: (name) => ApiService.getPlantsByName(name),
    getPlant: (id?: string) => get().plants.find((plant) => plant._id === id),
    createPlant: async (plant) => {
        const createdPlant = await ApiService.createPlant(JSON.stringify(plant));
        if (!createdPlant || typeof createdPlant._id !== "string") {
            throw new Error("Plant creation did not return a valid plant");
        }
        set((state) => ({
            plants: [...state.plants.filter((currentPlant) => currentPlant._id !== createdPlant._id), createdPlant],
        }));
        return createdPlant;
    },
    updatePlant: async (plant) => {
        const updatedPlant = await ApiService.updatePlant(JSON.stringify(plant));
        set((state) => ({
            plants: state.plants.map((currentPlant) =>
                currentPlant._id === plant._id ? plant : currentPlant
            ),
        }));
        return updatedPlant;
    },
    deletePlant: async (id) => {
        await ApiService.deletePlant(id);
        set((state) => ({
            plants: state.plants.filter((plant) => plant._id !== id),
            selectedPlantId: state.selectedPlantId === id ? undefined : state.selectedPlantId,
        }));
    },
    deletePlantsById: async (ids: string[]) => {
        await ApiService.deletePlantsById(ids);
        set((state) => ({
            plants: state.plants.filter((plant) => !ids.includes(plant._id)),
            selectedPlantId: ids.includes(state.selectedPlantId || "") ? undefined : state.selectedPlantId,
        }));
    }
}));